import { decodeFunctionData, encodeFunctionData, type Address, type Hex } from "viem";
import { ponsRewardNftABI } from "../../../abis/pons-reward-nft";
import { referralAuthorizationSchema, type ReferralAuthorization, type ReferralMilestone } from "../api/referral-schemas";
import type { Eip1193Provider } from "../../../wallet/eip1193";
import type { RpcReadOptions } from "../../../wallet/read-rpc";

export const REFERRAL_RECEIPT_PROTOCOL = "PENDING_MINT_TRANSFER_OPTIONAL_FINALIZATION_V1" as const;
export const REFERRAL_FINALIZED_RECEIPT_PROTOCOL = "MINT_TRANSFER_REQUIRED_FINALIZATION_V1" as const;
export const REFERRAL_EVIDENCE_MISSING = "Referral execution is unavailable: missing a real authorization call, verified signed program/milestone and replay binding, deployment/implementation admission and referral permission, and confirmed receipt/reward semantics.";

export type ReferralSignedAuthorization = Extract<ReturnType<typeof decodeFunctionData<typeof ponsRewardNftABI>>, { functionName: "mint" }>["args"][0];

/** Admitted source/deployment rules, never inferred from a prepared envelope or storage.
 * The verifier checks BE referral markers and the signed milestone/fee;
 * its admitted contract enforces a separate wallet + milestone replay key.
 */
export type ReferralMintEvidence = Readonly<{
  evidenceId: string; environment: string; chainId: number; collection: Address; sourceToken: Address;
  instanceId: Hex; signer: Address; policyHash: Hex; policyVersion: string;
  policyHashes?: readonly Hex[];
  executionEnabled: boolean;
  collectionCodeHash: Hex;
  /** Evidence-specific implementation/version check, including proxy upgrade binding when applicable. */
  verifyDeployment(provider: Eip1193Provider, assertCurrent: () => void, options: RpcReadOptions): Promise<void>;
  receiptProtocol: typeof REFERRAL_RECEIPT_PROTOCOL | typeof REFERRAL_FINALIZED_RECEIPT_PROTOCOL;
  verifySignedReferral(authorization: ReferralSignedAuthorization, milestone: ReferralMilestone): boolean;
}>;

export function decodeReferralMint(result: ReferralAuthorization, expected: {
  wallet: string; sourceToken: string | null; chainId: number; collection: string; milestone: ReferralMilestone;
}, evidence: ReferralMintEvidence | null = null) {
  referralAuthorizationSchema.parse(result);
  if (result.milestone !== expected.milestone || result.chainId !== expected.chainId || !same(result.contractAddress, expected.collection)) {
    throw new Error("Referral authorization milestone/deployment mismatch");
  }
  let decoded;
  try { decoded = decodeFunctionData({ abi: ponsRewardNftABI, data: result.call.data }); }
  catch { throw new Error("Invalid referral mint calldata"); }
  if (decoded.functionName !== "mint") throw new Error("Referral authorization is not a mint call");
  const [authorization, signature] = decoded.args;
  if (!same(authorization.wallet, expected.wallet) || expected.sourceToken !== null && !same(authorization.sourceToken, expected.sourceToken) ||
    authorization.selectedTierUsd !== BigInt(expected.milestone) || authorization.quotedMintFeeWei !== 0n ||
    authorization.deadline !== BigInt(result.deadline) || authorization.issuedAt >= authorization.deadline ||
    !/^0x[0-9a-f]{130}$/i.test(signature) ||
    encodeFunctionData({ abi: ponsRewardNftABI, functionName: "mint", args: decoded.args }).toLowerCase() !== result.call.data.toLowerCase()) {
    throw new Error("Referral calldata differs from the reviewed wallet, source, milestone, zero value or lifetime");
  }
  if (evidence && (!evidence.evidenceId.trim() ||
    ![REFERRAL_RECEIPT_PROTOCOL, REFERRAL_FINALIZED_RECEIPT_PROTOCOL].includes(evidence.receiptProtocol) ||
    evidence.chainId !== expected.chainId || !same(evidence.collection, expected.collection) ||
    expected.sourceToken === null || !same(evidence.sourceToken, expected.sourceToken) || !same(authorization.instanceId, evidence.instanceId) ||
    !(evidence.policyHashes ?? [evidence.policyHash]).some(hash => same(authorization.rewardPolicyHash, hash)) ||
    authorization.rewardPolicyVersion.toString() !== evidence.policyVersion ||
    !evidence.verifySignedReferral(authorization, expected.milestone))) {
    throw new Error("Referral signed program/milestone, replay binding or admitted deployment/policy mismatch");
  }
  return { authorization, signature, identity: evidence ? {
    route: "REFERRAL" as const, milestone: expected.milestone, proofKey: authorization.proofKey,
    authorizationId: authorization.authorizationId, tierUsd: String(expected.milestone), feeWei: "0",
    policyHash: authorization.rewardPolicyHash, policyVersion: authorization.rewardPolicyVersion.toString(),
    instanceId: authorization.instanceId, evidenceId: evidence.evidenceId, receiptProtocol: evidence.receiptProtocol,
  } : null };
}

export type ReferralMintIdentity = NonNullable<ReturnType<typeof decodeReferralMint>["identity"]>;
export function sameReferralIdentity(a: ReferralMintIdentity, b: object): boolean {
  return Object.entries(a).every(([key, value]) => {
    const other = (b as Record<string, unknown>)[key];
    return typeof value === "string" && value.startsWith("0x") && typeof other === "string" ? same(value, other) : value === other;
  });
}
function same(a: string, b: string) { return a.toLowerCase() === b.toLowerCase(); }
